import React, { useState } from 'react';
import { useCompany } from '../../lib/hooks';
import { supabase } from '../../lib/supabase';
import { Building2, Mail, Phone, Globe, Save, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';

const ProfileTab = () => {
  const { company } = useCompany();
  const [saving, setSaving] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    description: ''
  });

  // Initialize form data when company data loads
  React.useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || '',
        email: company.email || '',
        phone: company.phone || '',
        website: company.website || '',
        description: company.description || ''
      });
    }
  }, [company]);

  const handleSave = async () => {
    // Show confirmation dialog for name/email changes
    if (formData.name !== company?.name || formData.email !== company?.email) {
      setShowConfirmation(true);
      return;
    }

    await saveChanges();
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      // First update auth email if it changed
      if (formData.email !== company?.email) {
        const { error: updateAuthError } = await supabase.auth.updateUser({
          email: formData.email
        });

        if (updateAuthError) {
          throw new Error(`Failed to update email: ${updateAuthError.message}`);
        }

        toast({
          title: "Email Update",
          description: "Please check your new email for a confirmation link",
        });
      }

      // Then update company profile
      const { error: updateError } = await supabase
        .from('companies')
        .update({
          name: formData.name,
          phone: formData.phone,
          website: formData.website || null,
          description: formData.description || null
        })
        .eq('id', company?.id);

      if (updateError) throw updateError;
      
      toast({
        title: "Settings updated",
        description: "Your changes have been saved successfully.",
      });

      // Only reload if email didn't change (since email change requires verification)
      if (formData.email === company?.email) {
        window.location.reload();
      }
    } catch (err) {
      console.error('Error updating company:', err);
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to save changes",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
      setShowConfirmation(false);
    }
  };

  return (
    <>
      <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900">Company Profile</h2>
        <p className="text-muted-foreground">Update your company information</p>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>Update your company's basic details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label htmlFor="name">Company Name</Label>
              <div className="flex items-center space-x-2 mt-1.5">
                <Building2 className="w-5 h-5 text-muted-foreground" />
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter company name"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email Address</Label>
              <div className="flex items-center space-x-2 mt-1.5">
                <Mail className="w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter email address"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <div className="flex items-center space-x-2 mt-1.5">
                <Phone className="w-5 h-5 text-muted-foreground" />
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Enter phone number"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Additional Information</CardTitle>
            <CardDescription>Add more details about your business</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label htmlFor="website">Website</Label>
              <div className="flex items-center space-x-2 mt-1.5">
                <Globe className="w-5 h-5 text-muted-foreground" />
                <Input
                  id="website"
                  type="text"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description">Business Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="mt-1.5"
                placeholder="Tell us about your business..."
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Changes</DialogTitle>
            <DialogDescription>
              Please review the following changes carefully
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {formData.name !== company?.name && (
              <div>
                <p className="text-sm text-muted-foreground">Company Name Change:</p>
                <div className="flex items-center space-x-2 mt-1">
                  <p className="text-sm text-muted-foreground">{company?.name}</p>
                  <span>→</span>
                  <p className="text-sm font-medium">{formData.name}</p>
                </div>
              </div>
            )}
            {formData.email !== company?.email && (
              <div>
                <p className="text-sm text-muted-foreground">Email Change:</p>
                <div className="flex items-center space-x-2 mt-1">
                  <p className="text-sm text-muted-foreground">{company?.email}</p>
                  <span>→</span>
                  <p className="text-sm font-medium">{formData.email}</p>
                </div>
              </div>
            )}

            <Alert>
              <AlertTriangle className="h-5 w-5" />
              <AlertTitle>Important</AlertTitle>
              <AlertDescription>
                <p>These changes will affect:</p>
                <ul className="list-disc pl-5 mt-1 space-y-1">
                  <li>Your business website</li>
                  <li>Customer communications</li>
                  <li>Invoices and documents</li>
                </ul>
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmation(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={saveChanges}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Confirm Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProfileTab;